import {transparentize} from 'polished';
import React, {FC, useState} from 'react';
import styled, {css} from 'styled-components';
import {Key} from 'ts-key-enum';

import {buildInteractionIdProps} from '../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {
  AsyncHandlerOf,
  FrontKeyboardEvent,
  Handler,
  HandlerOf,
} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {BoxedInput} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {
  BoxedInputElement,
  BoxedInputElementTypesEnum,
} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInputElement';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {UserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useLocalize} from '../../../../srcCommon/internationalization/internationalizationContext';
import {updateName} from '../../../helpers/models/cardAttributeUpdateHelpers';
import {buildNoNameNoHandlesCardDisplayName} from '../../../helpers/models/cardHelpers';
import {
  ContactDetailsConflictingFieldKeysEnum,
  ContactDetailsSectionWithConflictsEnum,
} from '../../../helpers/models/cardMergeHelpers';
import {CardModel} from '../../../models/api/cardModel';
import {ContactModel} from '../../../models/api/contactModel';
import {VisualContactModel} from '../../../models/api/visualContactModel';
import {UpdateCardAttributeAsyncActionParams} from '../../../store/data/cards/cardsActions';
import {Localizer} from '../../../store/stateless/internationalizationSelectors';
import {EditInCrmButtonContainer} from '../../common/crm/buttons/editInCrmButtonContainer';
import {InlineEditingProps, useInlineEditing} from '../../common/inlineEditing/useInlineEditing';
import {useContactDetailsContext} from '../contactDetailsContext';
import {buildContactDetailsEditEvent} from '../contactDetailsTrackingHelpers';
import {ContactMergeConflictingValue} from '../contactMerge/contactMergeConflictingValue';

/*
 * Constants.
 */

const nameDisplayLineHeight = 30;

/*
 * Props.
 */

interface ContactHeaderNameProps {
  contact?: VisualContactModel;
  card: CardModel | undefined;
  crmSyncEditLink?: string;
  updateCardAttributeAsync: AsyncHandlerOf<UpdateCardAttributeAsyncActionParams>;
  onCreateNewCardAsync: (name: string, contact?: ContactModel) => Promise<CardModel | undefined>;
}

interface RenderNameAndEditControlsOptions {
  displayName: string;
  inlineEditingProps: InlineEditingProps;
  shouldShowControls: boolean;
  shouldShowNameMergeConflict: boolean;
  crmSyncEditLink?: string;
  card?: CardModel;
  onKeyDown: HandlerOf<FrontKeyboardEvent>;
  setShouldShowControls: HandlerOf<boolean>;
}

/*
 * Style.
 */

interface StyledNameHandleContainerDivProps {
  $isShowingMergeConflict: boolean;
  $hasAccountMergeConflict?: boolean;
}
const StyledNameHandleContainerDiv = styled.div<StyledNameHandleContainerDivProps>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 30px;
  margin: auto;
  ${(p) =>
    p.$isShowingMergeConflict &&
    css`
      margin-left: -8px;
    `}
  ${(p) =>
    p.$isShowingMergeConflict &&
    p.$hasAccountMergeConflict &&
    css`
      margin: -4px 0 4px -8px;
    `}
`;

const StyledNameHandleDisplayDiv = styled.div`
  line-height: ${nameDisplayLineHeight}px;
  font-size: ${fontSizes.veryLarge};
  font-weight: ${fontWeights.bold};
  ${ellipsis()}
`;

const StyledBoxedInputContainerDiv = styled.div`
  width: 100%;
  margin-left: -5px;
`;

const StyledBoxedInputElement = styled(BoxedInputElement)`
  margin: 0 5px;
  height: 30px;
  font-size: ${fontSizes.veryLarge};
  font-weight: ${fontWeights.bold};
  line-height: ${nameDisplayLineHeight}px;
`;

const StyledControlsDiv = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  margin: auto;
  padding-left: 16px;
  background: linear-gradient(
    to right,
    ${(p) => transparentize(1, p.theme.foreground)} 0%,
    ${(p) => p.theme.foreground} 16px
  );
  display: flex;
`;

const StyledIconButton = styled(IconButton)`
  margin-top: 3px;
`;

const StyledContactMergeConflictingValue = styled(ContactMergeConflictingValue)`
  font-size: ${fontSizes.veryLarge};
  font-weight: ${fontWeights.bold};
  line-height: ${nameDisplayLineHeight}px;
`;

/*
 * Component.
 */

export const ContactHeaderName: FC<ContactHeaderNameProps> = (props) => {
  const {contact, card, crmSyncEditLink, updateCardAttributeAsync, onCreateNewCardAsync} = props;
  const [shouldShowControls, setShouldShowControls] = useState(false);
  // We may need to override the name displayed in the case of a new card is being created and they
  // updated the name of the card.
  const [overriddenName, setOverriddenName] = useState<string>();
  const {panelId, isReadOnly, mergeContext, onTrackEvent} = useContactDetailsContext();

  const localize = useLocalize();
  const displayName = overriddenName ?? buildDisplayName(localize, card, contact);

  // Check if we are merging and have a merge conflict
  const conflictingHeaderValues =
    mergeContext?.conflictingMergeValues?.[ContactDetailsSectionWithConflictsEnum.HEADER];
  const shouldShowNameMergeConflict = Boolean(
    conflictingHeaderValues &&
      conflictingHeaderValues.some((value) => value.key === ContactDetailsConflictingFieldKeysEnum.NAME),
  );
  const hasAccountMergeConflict = Boolean(
    conflictingHeaderValues &&
      conflictingHeaderValues.some((value) => value.key === ContactDetailsConflictingFieldKeysEnum.ACCOUNT),
  );

  const onSaveAsync = async (name: string) => {
    // If we are trying to remove the name of the card but it has no name set.
    if (card && !name && !card.name) {
      return Promise.resolve();
    }

    // If we dont have a card and we do not have a name, do not create anything.
    if (!card && !name) {
      return Promise.resolve();
    }

    // If we do not have a card, we will try to create one first.
    // We need to do a local override of the name since it can take some time to create a new card.
    if (!card) {
      setOverriddenName(name);
      await onCreateNewCardAsync(name, contact?.contact);
      setOverriddenName(undefined);
      return Promise.resolve();
    }

    const action = updateName(displayName, name);
    if (onTrackEvent) {
      const event = buildContactDetailsEditEvent(action, card);
      onTrackEvent(event);
    }

    return updateCardAttributeAsync({
      card,
      action,
      shouldShowUndoBanner: true,
      panelId,
    });
  };

  const inlineEditingProps = useInlineEditing({
    currentAttributeValue: displayName,
    onSave: onSaveAsync,
  });

  const showControls = () => {
    if (isReadOnly || shouldShowNameMergeConflict || overriddenName) {
      return;
    }
    // Don't show the hover controls while the user is editing or saving
    if (inlineEditingProps.isEditing || inlineEditingProps.isSaving) {
      return;
    }

    setShouldShowControls(true);
  };

  const hideControls = () => {
    if (shouldShowControls) {
      setShouldShowControls(false);
    }
  };

  const onKeyDown = (event: FrontKeyboardEvent) => {
    switch (event.key) {
      case Key.Enter:
        event.preventDefault();
        inlineEditingProps.onEditSave(inlineEditingProps.editedValue);
        break;

      case Key.Escape:
        event.preventDefault();
        inlineEditingProps.onEditCancel();
        break;

      default:
      // Do nothing.
    }
  };

  return (
    <StyledNameHandleContainerDiv
      onMouseEnter={showControls}
      onMouseMove={showControls}
      onMouseLeave={hideControls}
      $isShowingMergeConflict={shouldShowNameMergeConflict}
      $hasAccountMergeConflict={hasAccountMergeConflict}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...buildInteractionIdProps('contact-details-contact-header-name-container')}
    >
      {maybeRenderMergeConflict(shouldShowNameMergeConflict)}
      {maybeRenderNameAndEditControls({
        displayName,
        inlineEditingProps,
        shouldShowNameMergeConflict,
        shouldShowControls,
        crmSyncEditLink,
        card,
        onKeyDown,
        setShouldShowControls,
      })}
    </StyledNameHandleContainerDiv>
  );
};

/*
 * Helpers.
 */

function buildDisplayName(localize: Localizer, card?: CardModel, contact?: VisualContactModel) {
  if (card && card.name) {
    return card.name;
  }
  if (contact) {
    const contactName = contact.name ?? contact?.contact.handle;
    if (contactName) {
      return contactName;
    }
  }
  return buildNoNameNoHandlesCardDisplayName(localize);
}

function maybeRenderMergeConflict(shouldShowNameMergeConflict: boolean) {
  if (!shouldShowNameMergeConflict) {
    return null;
  }
  return (
    <StyledContactMergeConflictingValue
      section={ContactDetailsSectionWithConflictsEnum.HEADER}
      sectionKey={ContactDetailsConflictingFieldKeysEnum.NAME}
      shouldRemoveTopAndBottomPadding
    />
  );
}

function maybeRenderNameAndEditControls(options: RenderNameAndEditControlsOptions) {
  const {
    displayName,
    inlineEditingProps,
    shouldShowNameMergeConflict,
    shouldShowControls,
    crmSyncEditLink,
    card,
    onKeyDown,
    setShouldShowControls,
  } = options;

  if (shouldShowNameMergeConflict) {
    return null;
  }

  if (inlineEditingProps.isEditing) {
    return (
      <StyledBoxedInputContainerDiv>
        <BoxedInput shouldAutoFocus noMargin>
          {(childProps) => (
            <StyledBoxedInputElement
              {...childProps}
              type={BoxedInputElementTypesEnum.TEXT}
              value={inlineEditingProps.editedValue}
              onChange={inlineEditingProps.onEdit}
              onBlur={() => inlineEditingProps.onEditSave(inlineEditingProps.editedValue)}
              onKeyDown={onKeyDown}
              size={VisualSizesEnum.LARGE}
            />
          )}
        </BoxedInput>
      </StyledBoxedInputContainerDiv>
    );
  }

  const onEditClick = () => {
    setShouldShowControls(false);
    inlineEditingProps.onEditStart();
  };

  return (
    <>
      <TooltipCoordinator render={() => displayName} placement="top">
        <StyledNameHandleDisplayDiv>
          <TooltipOverflowCondition>{displayName}</TooltipOverflowCondition>
        </StyledNameHandleDisplayDiv>
      </TooltipCoordinator>
      {shouldShowControls && !inlineEditingProps.isEditing && (
        <UserThemeConsumer>
          {(theme) => maybeRenderControls(theme, onEditClick, crmSyncEditLink, card)}
        </UserThemeConsumer>
      )}
    </>
  );
}

function maybeRenderControls(theme: UserTheme, onClick: Handler, crmSyncEditLink?: string, card?: CardModel) {
  if (crmSyncEditLink && card && card.externalInfo) {
    return (
      <StyledControlsDiv>
        <EditInCrmButtonContainer source={card.externalInfo.source} linkUrl={crmSyncEditLink} />
      </StyledControlsDiv>
    );
  }
  return (
    <StyledControlsDiv>
      <StyledIconButton
        interactionId="contact-details-header-edit-btn"
        iconName="editSmall"
        color={theme.greys.shade70}
        label={<Trans id="contact-details-contact-header-edit">Edit</Trans>}
        onClick={onClick}
      />
    </StyledControlsDiv>
  );
}
